import type { TripExpenseSplitItem, TripExpenseSplitMode } from "./tripStore";

type SplitRow = Pick<TripExpenseSplitItem, "participantId" | "participantName" | "value">;

export function currencyDecimals(currency: string): number {
  return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

export function roundCurrency(amount: number, currency: string): number {
  const scale = 10 ** currencyDecimals(currency);
  return Math.round((amount + Number.EPSILON) * scale) / scale;
}

export function equalPercentageRows<T extends SplitRow>(rows: T[]): T[] {
  const base = Math.floor(10000 / Math.max(1, rows.length));
  const remainder = 10000 - base * rows.length;
  return rows.map((row, index) => ({ ...row, value: (base + (index < remainder ? 1 : 0)) / 100 }));
}

export function computeSplit(amount: number, mode: TripExpenseSplitMode, rows: SplitRow[], currency = "EUR"): TripExpenseSplitItem[] {
  const seen = new Set<string>();
  const selected = rows.filter((row) => {
    if (!row.participantId || !row.participantName || !Number.isFinite(row.value) || row.value <= 0 || seen.has(row.participantId)) return false;
    seen.add(row.participantId);
    return true;
  });
  if (!Number.isFinite(amount) || amount <= 0 || !selected.length) return [];
  const scale = 10 ** currencyDecimals(currency);
  const totalUnits = Math.round((amount + Number.EPSILON) * scale);
  if (!Number.isSafeInteger(totalUnits) || totalUnits <= 0) return [];
  // Normalize large weights before summing to avoid overflow.
  const largestWeight = mode === "equal" ? 1 : Math.max(...selected.map(row => row.value));
  const weights = selected.map(row => mode === "equal" ? 1 : row.value / largestWeight);
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  const exact = weights.map(weight => totalUnits * (weight / totalWeight));
  const units = exact.map(value => Math.floor(value));
  let remaining = totalUnits - units.reduce((sum, value) => sum + value, 0);
  const order = exact.map((value, index) => ({ index, fraction: value - units[index] }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (let index = 0; remaining > 0; index++, remaining--) units[order[index % order.length].index]++;
  return selected.map((row, index) => ({ ...row, value: mode === "equal" ? 1 : row.value, amount: units[index] / scale }));
}
